import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Eye, RefreshCw, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { salesOrdersAPI } from '../services/api';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { formatCurrency } from '../utils/currency';
import { ModernButton, ModernCard, ModernInput, ModernLoadingSpinner, ModernModal, ModernTable } from '../components/ui/ModernComponents';

const VentesCommandesSupprimees = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const canRestore = user?.role === 'admin' || user?.role === 'manager';
  const canPermanentDelete = user?.role === 'admin' || user?.role === 'manager';

  const loadDeletedOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await salesOrdersAPI.getDeletedSalesOrders({ search: searchTerm || undefined });
      const payload = Array.isArray(res.data?.data) ? res.data.data : [];
      setOrders(payload);
    } catch (error) {
      console.error('Load deleted sales orders error:', error);
      toast.error('Erreur lors du chargement des commandes supprimées');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadDeletedOrders();
  }, [loadDeletedOrders]);

  const handleRestore = async (id) => {
    if (!canPermanentDelete) return;
    try {
      setRestoringId(id);
      await salesOrdersAPI.restoreSalesOrder(id);
      toast.success('Commande restaurée avec succès');
      await loadDeletedOrders();
    } catch (error) {
      console.error('Restore sales order error:', error);
      toast.error(error?.response?.data?.message || 'Erreur lors de la restauration');
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!canRestore) return;
    const confirmed = window.confirm('Supprimer définitivement cette commande de la base de données ? Cette action est irréversible.');
    if (!confirmed) return;
    try {
      setDeletingId(id);
      await salesOrdersAPI.permanentlyDeleteSalesOrder(id);
      toast.success('Commande supprimée définitivement');
      if (previewOrder?.id === id) {
        setPreviewOrder(null);
      }
      await loadDeletedOrders();
    } catch (error) {
      console.error('Permanent delete sales order error:', error);
      toast.error(error?.response?.data?.message || 'Erreur lors de la suppression définitive');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePreview = (order) => {
    setPreviewLoading(true);
    salesOrdersAPI
      .getSalesOrder(order.id, { include_deleted: 1 })
      .then((res) => setPreviewOrder(res.data || order))
      .catch((error) => {
        console.error('Load deleted order details error:', error);
        toast.error("Impossible d'ouvrir les détails de la commande");
      })
      .finally(() => setPreviewLoading(false));
  };

  const rows = useMemo(() => {
    return orders.map((o) => ({
      ...o,
      total_amount: Number(o.total_amount ?? o.total ?? 0),
    }));
  }, [orders]);

  const columns = useMemo(
    () => [
      { key: 'id', label: 'N°', render: (v) => <span className="font-semibold text-neutral-900">#{v}</span> },
      { key: 'client_name', label: 'Client', render: (v) => v || '-' },
      { key: 'client_phone', label: 'Téléphone', render: (v) => v || '-' },
      {
        key: 'created_at',
        label: 'Date commande',
        render: (v) => (v ? formatDateOnlyFr(v) : '-'),
      },
      {
        key: 'deleted_at',
        label: 'Date suppression',
        render: (v) => (v ? formatDateOnlyFr(v) : '-'),
      },
      {
        key: 'deleted_by_name',
        label: 'Supprimé par',
        render: (v) => v || '-',
      },
      {
        key: 'total_amount',
        label: 'Montant',
        render: (v) => formatCurrency(Number(v) || 0),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_v, row) => (
          <div className="flex items-center gap-2">
            <ModernButton
              type="button"
              size="sm"
              variant="secondary"
              icon={<Eye className="w-4 h-4" />}
              onClick={() => handlePreview(row)}
            >
              Détails
            </ModernButton>
            {canRestore ? (
              <ModernButton
                type="button"
                size="sm"
                variant="success"
                icon={restoringId === row.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                onClick={() => handleRestore(row.id)}
                disabled={restoringId === row.id}
              >
                Restaurer
              </ModernButton>
            ) : null}
            {canPermanentDelete ? (
              <ModernButton
                type="button"
                size="sm"
                variant="danger"
                icon={deletingId === row.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                onClick={() => handlePermanentDelete(row.id)}
                disabled={deletingId === row.id || restoringId === row.id}
              >
                Supprimer
              </ModernButton>
            ) : null}
          </div>
        ),
      },
    ],
    [canRestore, canPermanentDelete, deletingId, restoringId, previewOrder, loadDeletedOrders]
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Trash2 className="w-6 h-6 text-red-600" />
            Commandes supprimées
          </h1>
          <p className="text-neutral-600">
            Liste des commandes supprimées (soft delete). {canRestore ? 'Vous pouvez les restaurer.' : 'Vous pouvez seulement les consulter.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModernInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par N°, client, téléphone..."
            icon={<Search className="w-4 h-4 text-neutral-400" />}
          />
          <ModernButton type="button" variant="secondary" onClick={loadDeletedOrders}>
            Rafraîchir
          </ModernButton>
        </div>
      </div>

      <ModernCard>
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <ModernLoadingSpinner size="lg" />
          </div>
        ) : (
          <ModernTable
            data={rows}
            columns={columns}
            emptyMessage="Aucune commande supprimée"
          />
        )}
      </ModernCard>

      <ModernModal
        isOpen={Boolean(previewOrder)}
        onClose={() => setPreviewOrder(null)}
        title={previewOrder ? `Détails commande supprimée #${previewOrder.id}` : 'Détails commande supprimée'}
        size="xxl"
      >
        {previewLoading ? (
          <div className="py-16 flex items-center justify-center">
            <ModernLoadingSpinner size="lg" />
          </div>
        ) : previewOrder ? (
          <div className="space-y-6">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Cette commande est supprimée (soft delete) - lecture seule.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ModernInput label="N° Commande" value={`#${previewOrder.id}`} disabled />
              <ModernInput label="Client" value={previewOrder.client_name || '-'} disabled />
              <ModernInput label="Téléphone" value={previewOrder.client_phone || '-'} disabled />
              <ModernInput label="Statut" value={previewOrder.status || '-'} disabled />
              <ModernInput label="Date commande" value={previewOrder.created_at ? formatDateOnlyFr(previewOrder.created_at) : '-'} disabled />
              <ModernInput label="Date suppression" value={previewOrder.deleted_at ? formatDateOnlyFr(previewOrder.deleted_at) : '-'} disabled />
              <ModernInput label="Supprimé par" value={previewOrder.deleted_by_name || '-'} disabled />
              <ModernInput label="Total" value={formatCurrency(Number(previewOrder.total ?? previewOrder.total_amount ?? 0))} disabled />
              <ModernInput label="Payé" value={formatCurrency(Number(previewOrder.total_paid ?? 0))} disabled />
            </div>
            <div>
              <h4 className="font-semibold text-neutral-900 mb-2">Articles</h4>
              <ModernTable
                data={Array.isArray(previewOrder.items) ? previewOrder.items : []}
                columns={[
                  { key: 'product_name', label: 'Produit', render: (v, row) => v || `#${row.product_id || '-'}` },
                  { key: 'item_type', label: 'Type', render: (v) => v || '-' },
                  { key: 'side', label: 'Côté', render: (v) => v || '-' },
                  {
                    key: 'verre_config',
                    label: 'Config verre',
                    render: (_v, row) => {
                      if (row.item_type !== 'verre') return '-';
                      const side = String(row.side || '').toLowerCase();
                      const sideSph = side === 'left'
                        ? previewOrder?.left_sph
                        : side === 'right'
                          ? previewOrder?.right_sph
                          : null;
                      const sideCyl = side === 'left'
                        ? previewOrder?.left_cyl
                        : side === 'right'
                          ? previewOrder?.right_cyl
                          : null;
                      const sideAxis = side === 'left'
                        ? previewOrder?.left_axis
                        : side === 'right'
                          ? previewOrder?.right_axis
                          : null;
                      const sideAdd = side === 'left'
                        ? previewOrder?.left_addition
                        : side === 'right'
                          ? previewOrder?.right_addition
                          : null;

                      const sph = row.sph ?? sideSph ?? '-';
                      const cyl = row.cyl ?? sideCyl ?? '-';
                      const axis = row.axis ?? row.axe ?? sideAxis ?? '-';
                      const add = row.addition ?? sideAdd ?? '-';
                      const remarque = row.remarque || '-';
                      return (
                        <div className="text-xs leading-5 text-neutral-700">
                          <div>SPH: {sph}</div>
                          <div>CYL: {cyl}</div>
                          <div>AXE: {axis}</div>
                          <div>ADD: {add}</div>
                          <div>Remarque: {remarque}</div>
                        </div>
                      );
                    },
                  },
                  { key: 'quantity', label: 'Qté', render: (v) => Number(v) || 0 },
                  { key: 'price', label: 'Prix', render: (v) => formatCurrency(Number(v) || 0) },
                ]}
                emptyMessage="Aucun article"
              />
            </div>
          </div>
        ) : null}
      </ModernModal>

    </div>
  );
};

export default VentesCommandesSupprimees;
